import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  ArrowLeft, Lock, MapPin, Minus, Package, Plus, ShoppingCart, Trash2,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger,
} from '../ui/sheet';
import { partners } from '../../data/mock';
import { CrediscopBadge } from '../CrediscopBadge';
import { CartReservationCountdown } from './CartReservationCountdown';
import { ReturnCodeBox } from './ReturnCodeBox';
import { NotificationsBell } from '../NotificationsBell';
import NavigationHistoryDropdown from '../NavigationHistoryDropdown';
import { formatPrice } from './catalogUtils';
import { CartSuggestions } from './CartSuggestions';
import { BrandLogos } from '../BrandLogos';
import { HeaderBackButton } from '../HeaderBackButton';
import LanguageSwitcher from '../LanguageSwitcher';
import i18n from '@/i18n';
import { langKey } from '@/i18n/fmt';
import { authAPI } from '../../services/api';
import { getMySpace, isAdminUser } from '../navbar/navItems';

export const CatalogHeader = ({
  zones, entitledZones, selectedZone, setSelectedZone, cart, cartOpen, setCartOpen,
  cartLoading, cartItemCount, cartTotal, handleUpdateQuantity,
  handleRemoveFromCart, handleAddToCart, navigate, products = [],
}) => {
  const connected = authAPI.isAuthenticated();
  const [me, setMe] = useState(null);
  useEffect(() => { if (connected) authAPI.getMe().then(setMe).catch(() => {}); }, [connected]);
  const mySpace = getMySpace(me);
  const admin = isAdminUser(me);
  const cartItemName = (item) => {
    const p = products.find((x) => x.sku === item.product_sku);
    return p?.translations?.[langKey()]?.name || item.product_name;
  };
  return (
      <header 
        className="sticky top-0 z-50"
        style={{
          background: 'rgba(30,12,52,0.88)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(212,175,55,0.32)'
        }}
      >
        <div className="max-w-[1280px] mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <HeaderBackButton fallback={connected ? mySpace : '/'} />
            <div className="flex items-center gap-3">
              <BrandLogos />
              <LanguageSwitcher className="hidden md:flex" />
            </div>
          </div>
          
          {/* Quick Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link to="/" className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
              {i18n.t('nav.home')}
            </Link>
            {!connected && (
              <>
                <Link to="/kdmarche" className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-testid="catalog-nav-kdmarche">
                  KDMARCHÉ
                </Link>
                <Link to="/tarifs" className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-testid="catalog-nav-acces-pro">
                  {i18n.t('catalog.acces_pro')}
                </Link>
                <Link to="/contact" className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-testid="catalog-nav-contact">
                  {i18n.t('footer.contact')}
                </Link>
              </>
            )}
            {connected && (
              <>
            <Link to={mySpace} className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-testid="catalog-nav-my-space">
              {i18n.t('catalog.mon_espace')}
            </Link>
            <Link to="/commandes" className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
              {i18n.t('catalog.commandes')}
            </Link>
            <Link to="/wallet" className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
              CREDI&rsquo;SCOP
            </Link>
            {(me?.role === 'vendor' || admin) && (
              <Link to="/espace-vendeur" className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
                {i18n.t('catalog.vendeur')}
              </Link>
            )}
            {admin && (
              <Link to="/superadmin" className="px-3 py-1.5 text-xs text-[#E9CF8E]/80 hover:text-[#E9CF8E] hover:bg-white/5 rounded-lg transition-colors" data-testid="catalog-nav-superadmin">
                Super Admin
              </Link>
            )}
              </>
            )}
          </nav>
          
          <div className="flex items-center gap-3">
            {connected && <CrediscopBadge className="hidden sm:inline-flex" />}
            {connected && <NotificationsBell />}
            {/* Navigation History */}
            {connected && <NavigationHistoryDropdown variant="dark" />}
            
            {/* Zone selector */}
            <Select value={selectedZone} onValueChange={setSelectedZone}>
              <SelectTrigger className="w-[160px] h-10 bg-white/[0.04] border-white/10 text-white">
                <MapPin className="w-4 h-4 mr-2 text-[#D4AF37]" />
                <SelectValue placeholder="Zone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL" data-testid="zone-option-ALL">
                  <span className="inline-flex items-center gap-1.5">{i18n.t('catalog.all_territories')}</span>
                </SelectItem>
                {zones.map(zone => {
                  const locked = Array.isArray(entitledZones) && !entitledZones.includes(zone.code);
                  return (
                    <SelectItem key={zone.code} value={zone.code}
                      data-testid={`zone-option-${zone.code}`}>
                      <span className="inline-flex items-center gap-1.5">
                        {locked && <Lock className="w-3 h-3 text-[#D9B35A]" />}
                        {zone.name}{locked ? ` ${i18n.t('catalog.add_zone')}` : ''}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {/* Cart button (membres) / Connexion (visiteurs) */}
            {connected ? (
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  className="relative bg-[#D9B35A]/20 border-[#D9B35A]/30 text-[#D9B35A] hover:bg-[#D9B35A]/30"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#D9B35A] text-black text-xs font-bold flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg bg-[#0a0d14] border-white/10">
                <SheetHeader>
                  <SheetTitle className="text-white flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-[#D9B35A]" />
                    {i18n.t('catalog.cart_title', { n: cartItemCount })}
                  </SheetTitle>
                  <SheetDescription className="text-white/60">
                    {i18n.t('catalog.cart_zone')} {selectedZone === 'ALL' ? i18n.t('catalog.all_territories') : (zones.find(z => z.code === selectedZone)?.name || selectedZone)}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 flex flex-col h-[calc(100vh-200px)]">
                  {cart.items?.length > 0 && (
                    <>
                      <CartReservationCountdown reservedUntil={cart.reserved_until} zone={cart.zone_code} />
                      <ReturnCodeBox cart={cart} zone={cart.zone_code} />
                    </>
                  )}
                  {cart.alerts?.length > 0 && (
                    <div className="mb-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-1" data-testid="cart-alerts-banner">
                      {cart.alerts.map((a) => (
                        <p key={`${a.type}-${a.item_id}`} className={`text-xs ${a.type === 'UNAVAILABLE' ? 'text-red-400' : 'text-amber-400'}`}>
                          {a.type === 'PRICE_CHANGED' && i18n.t('catalog.price_changed', { p: a.product_name, a: formatPrice(a.old_price_ht_cents), b: formatPrice(a.new_price_ht_cents) })}
                          {a.type === 'UNAVAILABLE' && i18n.t('catalog.unavailable_alert', { p: a.product_name })}
                          {a.type === 'AVAILABLE_AGAIN' && i18n.t('catalog.available_again', { p: a.product_name })}
                        </p>
                      ))}
                    </div>
                  )}
                  {cart.items?.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center text-white/50">
                      <div className="text-center">
                        <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{i18n.t('catalog.cart_empty')}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 overflow-auto space-y-3">
                        {cart.items?.map(item => (
                          <div 
                            key={item.id} 
                            className={`p-3 rounded-xl border ${item.unavailable ? 'bg-red-500/[0.06] border-red-500/25' : 'bg-white/[0.04] border-white/[0.08]'}`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <p className="font-medium text-white/90 text-sm">{cartItemName(item)}</p>
                                <p className="text-xs text-white/50">{item.product_sku}</p>
                                {item.unavailable && (
                                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400" data-testid={`cart-item-unavailable-${item.id}`}>
                                    {i18n.t('catalog.unavailable')}
                                  </span>
                                )}
                              </div>
                              <button 
                                onClick={() => handleRemoveFromCart(item.id)}
                                data-testid={`cart-item-remove-${item.id}`}
                                className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <button 
                                  onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                  className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center hover:bg-white/[0.12]"
                                  disabled={cartLoading}
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="w-8 text-center font-medium">{item.quantity}</span>
                                <button 
                                  onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                  className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center hover:bg-white/[0.12]"
                                  disabled={cartLoading}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                              <p className="font-semibold text-[#D9B35A]">
                                {formatPrice(item.line_total_ht_cents || item.price_ht_cents * item.quantity)} HT
                              </p>
                            </div>
                          </div>
                        ))}

                        <CartSuggestions
                          cart={cart}
                          cartLoading={cartLoading}
                          onAddProduct={handleAddToCart}
                        />
                      </div>

                      <div className="pt-4 border-t border-white/[0.08] space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">{i18n.t('catalog.total_ht')}</span>
                          <span className="text-xl font-bold text-[#D9B35A]">{formatPrice(cartTotal)}</span>
                        </div>
                        <Button 
                          className="w-full bg-[#D9B35A] hover:bg-[#c9a34a] text-black font-semibold"
                          onClick={() => navigate('/checkout')}
                          disabled={cart.items?.length === 0 || cart.items?.some(i => i.unavailable)}
                          data-testid="checkout-button"
                        >
                          {i18n.t('catalog.order_exw')}
                        </Button>
                        <p className="text-xs text-white/40 text-center">
                          {i18n.t('catalog.order_note')}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
            ) : (
              <span className="flex items-center gap-2">
                <Link to="/connexion" data-testid="catalog-login-btn"
                  className="px-3 py-2 rounded-lg text-xs font-semibold text-white/80 border border-white/20 hover:bg-white/10 transition-colors">
                  {i18n.t('nav.login')}
                </Link>
                <Link to="/adhesion" data-testid="catalog-join-btn"
                  className="px-3 py-2 rounded-lg text-xs font-bold text-black bg-[#D9B35A] hover:bg-[#c9a34a] transition-colors">
                  {i18n.t('catalog.adherer')}
                </Link>
              </span>
            )}
          </div>
        </div>
      </header>
  );
};
